// The app shell's shape for the window it is in (docs/specs/responsive-windowed.md,
// James 2026-10-19: "Rearrange to fit" — the side menu collapses to an icon
// rail, then to a ☰ drawer, as the window narrows; any resolution).
//
// Pure rules + the localStorage choice, so they can be tested without
// mounting Layout (lib/shellLayout.test.js); hooks/useShellLayout.js watches
// the window and runs them.
//
//   window width      tier      menu (unless the person chose otherwise)
//   >= 1280 px        wide      full menu          (« collapses it to the rail)
//   768 - 1279 px     medium    icon rail          (» expands it to the full menu)
//   < 768 px          narrow    ☰ drawer (overlay) — no choice to remember
//
// 1280 keeps the full menu on a maximised 1366x768 laptop and at 1080p (the
// Reports page was fitted at both); 768 is where the drawer always started
// (Tailwind `md`).  Both are CSS px, the same unit the media queries use.

export const RAIL_MIN_WIDTH = 768
export const FULL_MIN_WIDTH = 1280
export const RAIL_QUERY = `(min-width: ${RAIL_MIN_WIDTH}px)`
export const FULL_QUERY = `(min-width: ${FULL_MIN_WIDTH}px)`

// The person's own collapse choice, ONE PER TIER, so a choice made in one
// window size never forces the other: collapsing a wide window and later
// narrowing then widening it again comes back collapsed; expanding the rail on
// a medium window is kept for medium windows only.
//   lextrack_sidebar_collapsed         wide windows:   '1' = rail (the person
//                                      collapsed it), anything else = full.
//                                      The key predates this file and keeps
//                                      its meaning, so existing choices stand.
//   lextrack_sidebar_collapsed_medium  medium windows: '0' = full menu (the
//                                      person expanded it), anything else =
//                                      rail.
// Both are registered in lib/uiPrefs.js (Layout + navigation), so "Reset UI
// preferences" clears them; Layout re-reads on that reset's key-null event.
export const WIDE_COLLAPSED_KEY = 'lextrack_sidebar_collapsed'
export const MEDIUM_COLLAPSED_KEY = 'lextrack_sidebar_collapsed_medium'
export const SIDEBAR_PREF_KEYS = Object.freeze([WIDE_COLLAPSED_KEY, MEDIUM_COLLAPSED_KEY])

// 'wide' | 'medium' | 'narrow' for a width in CSS px.  A missing or
// unreadable width counts as wide (the old desktop layout).
export function viewportTier (width) {
  const w = Number(width)
  if (!Number.isFinite(w) || w <= 0) return 'wide'
  if (w >= FULL_MIN_WIDTH) return 'wide'
  if (w >= RAIL_MIN_WIDTH) return 'medium'
  return 'narrow'
}

// The tier of the window this code runs in: matchMedia when the browser has
// it (the same answer CSS gets), else innerWidth.
export function readViewportTier () {
  if (typeof window === 'undefined') return 'wide'
  try {
    if (typeof window.matchMedia === 'function') {
      if (window.matchMedia(FULL_QUERY).matches) return 'wide'
      return window.matchMedia(RAIL_QUERY).matches ? 'medium' : 'narrow'
    }
  } catch { /* fall through to innerWidth */ }
  return viewportTier(window.innerWidth)
}

// { wideCollapsed, mediumCollapsed } — each the person's choice for that tier
// (true = rail).  Unset, garbage or a throwing localStorage give the defaults:
// full on wide windows, rail on medium ones.
export function readSidebarPrefs (storage) {
  const read = (key) => {
    try {
      const s = storage || (typeof window !== 'undefined' ? window.localStorage : null)
      return s ? s.getItem(key) : null
    } catch { return null }
  }
  return {
    wideCollapsed: read(WIDE_COLLAPSED_KEY) === '1',
    mediumCollapsed: read(MEDIUM_COLLAPSED_KEY) !== '0',
  }
}

// The same prefs with this tier's choice set (a new object; narrow has none).
export function withSidebarChoice (prefs, tier, collapsed) {
  const p = prefs || { wideCollapsed: false, mediumCollapsed: true }
  if (tier === 'wide') return { ...p, wideCollapsed: !!collapsed }
  if (tier === 'medium') return { ...p, mediumCollapsed: !!collapsed }
  return p
}

// Store this tier's choice.  Returns false when storage refused (private mode
// etc.) — the choice still applies for this page's life.
export function writeSidebarChoice (tier, collapsed, storage) {
  const key = tier === 'wide' ? WIDE_COLLAPSED_KEY : tier === 'medium' ? MEDIUM_COLLAPSED_KEY : null
  if (!key) return false
  try {
    const s = storage || window.localStorage
    s.setItem(key, collapsed ? '1' : '0')
    return true
  } catch { return false }
}

// 'full' | 'rail' | 'drawer' — what the side menu is in this tier.
export function sidebarModeFor (tier, prefs) {
  const p = prefs || {}
  if (tier === 'narrow') return 'drawer'
  if (tier === 'medium') return p.mediumCollapsed === false ? 'full' : 'rail'
  return p.wideCollapsed === true ? 'rail' : 'full'
}

// Whether a storage event concerns the sidebar choice (its own keys, or the
// key-null event of a bulk clear / "Reset UI preferences").  Anything else is
// ignored so other windows' timer heartbeats never re-render the shell.
export function isSidebarPrefEvent (e) {
  if (!e) return false
  return e.key === null || e.key === undefined || SIDEBAR_PREF_KEYS.includes(e.key)
}

// ── Focus trap for the overlay menu (drawer, or the rail opened for search) ──
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

// The elements Tab can land on inside `root`, in order.  In a browser an
// element with no box (display:none, or inside one) is skipped; jsdom lays
// nothing out, so there every element counts.
export function focusableWithin (root) {
  if (!root || typeof root.querySelectorAll !== 'function') return []
  const all = Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR))
  let layoutKnown = false
  try { layoutKnown = document.documentElement.getClientRects().length > 0 } catch { layoutKnown = false }
  // Hidden by an attribute on itself or on anything between it and root.
  const hiddenBelowRoot = (el) => {
    for (let n = el; n && n !== root; n = n.parentElement) {
      if (n.hasAttribute('inert') || n.hasAttribute('hidden') || n.getAttribute('aria-hidden') === 'true') return true
    }
    return false
  }
  return all.filter((el) => {
    if (hiddenBelowRoot(el)) return false
    if (!layoutKnown) return true
    try { return el.getClientRects().length > 0 } catch { return true }
  })
}

// Where Tab / Shift+Tab should go next to stay inside `root` (null = leave
// the browser alone).  `active` is document.activeElement.
export function trapTabTarget (root, active, shiftKey) {
  const list = focusableWithin(root)
  if (list.length === 0) return root || null
  const first = list[0]
  const last = list[list.length - 1]
  if (!root.contains(active)) return shiftKey ? last : first
  if (shiftKey && (active === first || active === root)) return last
  if (!shiftKey && active === last) return first
  return null
}
